import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ReplaySubject, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { createGuid } from '../../../core/helpers/utility';
import { ICampaign, INotes } from '../../../core/models/campaigns.model';
import { NOTES_TYPE, ROLES_TYPE } from '../../../core/helpers/constants';
import { DataFeedService } from '../../api/datafeed.service';
import * as util from '../../../core/helpers/utility';
import * as moment from 'moment';
import * as _ from 'underscore';
import { UsersApiService } from '../../../pages/users/users.apiservice';
import { CampaignsApiService } from '../../../pages/campaigns/campaigns.apiservice';
import { ClientsApiService } from '../../../pages/clients/clients.apiservice';
import { IClient } from '../../../core/models/clients.models';
import { InstanceService } from '../../../core/instance/instance.service';
import { EmailRequestModel, FormsApiService } from '../../../pages/forms/forms.apiservice';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'modal-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss']
})
export class SendEmailModal implements OnInit, AfterViewInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private normalizeReplyToAddress(email: string): string {
    const trimmed = (email ?? '').toString().trim();
    if (!trimmed || !this.instance.is('prspr')) {
      return trimmed;
    }

    return trimmed.replace(/franchisefocusedmarketing\.com/ig, 'prsprmarketing.com');
  }

  emailForm!: FormGroup;
  submitted = false;
  isSubmitting = false;
  submitError: string | null = null;
  aiRewriteError: string | null = null;
  isRewritingWithAi = false;
  isGeneratingDraft = false;
  aiRewritePrompt = '';
  aiRewriteSystemContent = '';
  aiRewriteTemperature = 0.5;
  aiRewriteModel: 'gpt-5-mini' | 'gpt-5' | 'gpt-5.6-luna' | 'gpt-5.6-terra' = 'gpt-5';
  campaignData: ICampaign = {} as ICampaign;
  clientData: IClient = {} as IClient;
  allClients: Array<IClient> = new Array<IClient>();
  toEmailAddress: string = '';
  ccEmailAddresses: string[] = [];
  hasEmployees: boolean = false;

  selectedToClientId: string | null = null;

  private normalizeClientId(selection: any): string | null {
    if (selection === null || selection === undefined) return null;
    if (typeof selection === 'string') {
      const trimmed = selection.trim();
      return trimmed ? trimmed : null;
    }
    if (typeof selection === 'number') {
      const str = selection.toString();
      return str ? str : null;
    }
    if (typeof selection === 'object') {
      const maybeId = (selection as any)?.id ?? (selection as any)?.value;
      if (typeof maybeId === 'string') {
        const trimmed = maybeId.trim();
        return trimmed ? trimmed : null;
      }
      if (typeof maybeId === 'number') return maybeId.toString();
    }
    return null;
  }

  onToClientSelected(selection: any): void {
    const id = this.normalizeClientId(selection);
    if (!id) return;

    this.clientService.getClientDetails(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.clientData = response?.data;
        this.iClientId = id;
        const email = (response?.data?.email ?? '').toString();
        if (email) {
          this.toEmailAddress = email;
          this.emailForm.get('to')?.setValue(this.toEmailAddress);
        }
        this.hasEmployees = Array.isArray(response?.data?.employees) && response.data.employees.length > 0;
      });
  }

  private extractEmployeeIds(employees: any): string[] {
    const raw: any[] = Array.isArray(employees) ? employees : [];
    const ids = raw
      .map((e: any) => {
        if (!e) return null;
        if (typeof e === 'string') return e;
        if (typeof e === 'object') return e.id ?? e.userId ?? e.employeeId ?? null;
        return null;
      })
      .map((id: any) => (id ?? '').toString().trim())
      .filter((id: string) => !!id);

    // Unique
    return Array.from(new Set(ids));
  }

  loadEmployeesCc(): void {
    const campaignId = (this.iCampaignId ?? '').toString().trim();
    if (campaignId) {
      const employeeIds = this.extractEmployeeIds((this.campaignData as any)?.employees);
      if (employeeIds.length > 0) {
        this.populateCcFromUserIds(employeeIds);
        return;
      }
    }

    const clientId = (this.iClientId ?? '').toString().trim();
    if (clientId) {
      const employeeIds = this.extractEmployeeIds((this.clientData as any)?.employees);
      if (employeeIds.length > 0) {
        this.populateCcFromUserIds(employeeIds);
        return;
      }
    }
  }

  private populateCcFromUserIds(userIds: string[]): void {
    if (!userIds || userIds.length === 0) return;

    forkJoin(
      userIds.map(id =>
        this.usersApiService.getUserDetails(id).pipe(
          map((resp: any) => (resp?.data?.email ?? '').toString().trim()),
          catchError(() => of(''))
        )
      )
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe((emails: string[]) => {
        const cleaned = (emails ?? [])
          .map(e => (e ?? '').toString().trim())
          .filter(e => !!e);

        const unique = Array.from(new Set(cleaned));
        const ccValue = unique.join(';');

        this.ccEmailAddresses = unique;
        this.emailForm.get('cc')?.setValue(ccValue);
      });
  }

  // Not using the Templates for now - title is localized but template selection is hidden, but keeping the code in place for easy re-implementation if needed in the future
  emailTemplates: Array<{
    title: string;
    description: string;
    subject?: string;
    isInternal?: boolean;
    pin?: boolean;
  }> = [];

  sortedEmailTemplates: Array<{
    title: string;
    description: string;
    subject?: string;
    isInternal?: boolean;
    pin?: boolean;
  }> = [];

  private applyingTemplate = false;
  private pendingTitleTerm = '';

  private _iClientId: string = '';
  private _iCampaignId: string = '';
  private _iSubject: string = '';
  private _iFromName: string = '';

  private syncToClientDropdownFromClientId(): void {
    const id = this.normalizeClientId(this._iClientId);
    this.selectedToClientId = id;
  }

  private patchIdsIntoForm(): void {
    if (!this.emailForm) return;
    this.emailForm.patchValue(
      {
        clientId: this._iClientId || null,
        campaignId: this._iCampaignId || null,
      },
      { emitEvent: false }
    );
  }

  private patchSubjectIntoForm(): void {
    if (!this.emailForm) return;
    const subject = (this._iSubject ?? '').toString();
    if (!subject) return;
    this.emailForm.patchValue({ subject }, { emitEvent: false });
  }

  private getEffectiveClientId(): string | null {
    return this.normalizeClientId(this._iClientId) ?? this.normalizeClientId(this.selectedToClientId);
  }

  private formatDefaultFromName(name: string | null | undefined): string {
    const trimmedName = String(name ?? '').trim();
    if (!trimmedName) {
      return 'REACH';
    }

    return /via\s+reach$/i.test(trimmedName) ? trimmedName : `${trimmedName} via REACH`;
  }

  private patchDefaultFromName(name: string | null | undefined): void {
    const control = this.emailForm?.get('fromName');
    if (!control) {
      return;
    }

    const currentValue = String(control.value ?? '').trim();
    if (currentValue && control.dirty) {
      return;
    }

    control.setValue(this.formatDefaultFromName(name), { emitEvent: false });
  }

  private buildHtmlBodyFromPlain(plainText: unknown): string {
    const escaped = String(plainText ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    return `<div style="white-space:pre-wrap">${escaped}</div>`;
  }

  private buildEmailRequest(noteRequest: any): EmailRequestModel | null {
    const to = String(noteRequest?.to ?? '').trim();
    const subject = String(noteRequest?.subject ?? '').trim();
    const replyTo = String(noteRequest?.replyTo ?? '').trim();
    const plain = String(noteRequest?.description ?? '').trim();
    const cc = String(noteRequest?.cc ?? '').trim();
    const fromName = String(noteRequest?.fromName ?? '').trim() || 'REACH';

    if (!to || !subject || !plain) {
      return null;
    }

    return {
      ToEmail: to,
      Subject: subject,
      ReplyToEmail: replyTo,
      PlainContent: plain,
      HtmlContent: this.buildHtmlBodyFromPlain(plain),
      CcEmails: cc,
      BccEmails: '',
      FromName: fromName,
    };
  }

  @Input()
  set iClientId(value: string) {
    this._iClientId = (value ?? '').toString();
    this.patchIdsIntoForm();
    this.syncToClientDropdownFromClientId();
  }
  get iClientId(): string {
    return this._iClientId;
  }

  @Input()
  set iCampaignId(value: string) {
    this._iCampaignId = (value ?? '').toString();
    this.patchIdsIntoForm();
  }
  get iCampaignId(): string {
    return this._iCampaignId;
  }

  @Input()
  set iSubject(value: string) {
    this._iSubject = (value ?? '').toString();
    this.patchSubjectIntoForm();
  }
  get iSubject(): string {
    return this._iSubject;
  }

  @Input()
  set iFromName(value: string) {
    this._iFromName = (value ?? '').toString();
    this.patchDefaultFromName(this._iFromName);
  }
  get iFromName(): string {
    return this._iFromName;
  }

  @Input() iLeadId: string = '';
  @Input() iType: number = 2;
  @Input() iActiveUserExternal: number = ROLES_TYPE.External;
  @Input() iIsAdmin: boolean = true;
  private _iAiRewritePrompt = '';
  private _iAiRewriteSystemContent = '';
  private _iAiRewriteTemperature = 0.5;

  @Input()
  set iAiRewritePrompt(value: string) {
    this._iAiRewritePrompt = value ?? '';
    this.aiRewritePrompt = String(value ?? '').trim() || this.aiRewritePrompt;
  }
  get iAiRewritePrompt(): string { return this._iAiRewritePrompt; }

  @Input()
  set iAiRewriteSystemContent(value: string) {
    this._iAiRewriteSystemContent = value ?? '';
    this.aiRewriteSystemContent = String(value ?? '').trim() || this.aiRewriteSystemContent;
  }
  get iAiRewriteSystemContent(): string { return this._iAiRewriteSystemContent; }

  @Input()
  set iAiRewriteTemperature(value: number) {
    this._iAiRewriteTemperature = value;
    if (value !== null && value !== undefined) {
      this.aiRewriteTemperature = value;
    }
  }
  get iAiRewriteTemperature(): number { return this._iAiRewriteTemperature; }
  @Input() set iNote(note: INotes) {
    if (note) {
      this.emailForm.patchValue(note);
      if (note.noteFiles)
        this.files = note.noteFiles;
    }
  }
    generateDraftWithOverlay(): void {
      this.isGeneratingDraft = true;
      this.rewriteDescriptionWithAi();
    }


  constructor(
    private dataFeedService: DataFeedService,
    public activeModal: NgbActiveModal,
    private usersApiService: UsersApiService,
    private campaignService: CampaignsApiService,
    private clientService: ClientsApiService,
    private instance: InstanceService,
    private formsApiService: FormsApiService,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private http: HttpClient) {
    this.emailForm = this.formBuilder.group({
      id: [null],
      to: [null, [Validators.required]],
      cc: [null],
      replyTo: [null],
      fromName: ['REACH'],
      type: [null],
      campaignId: [null],
      clientId: [null],
      leadId: [null],
      assignedUserId: ['', [Validators.required]],
      subject: [null, [Validators.required]],
      title: ['Email Note', [Validators.required]],
      description: [null, [Validators.required]],
      isInternal: [null],
      pin: false,
    });

    this.dataFeedService.getAllClients()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        if (response.good) {
          this.allClients = _.sortBy([...response.data], 'firstName').map(i => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
        }
      });

    this.emailTemplates = [
      {
        title: 'Ad Approval Email',
        subject: 'Ad Approval Needed',
        description: '',
      },
      {
        title: 'Billing Email',
        description: '',
        pin: true,
      },
      {
        title: 'Email Note',
        description: '',
      },
    ];
    this.sortedEmailTemplates = [...this.emailTemplates].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));

    this.activeUserId = util.getActiveUser().id;
    if (this.activeUserId) {
      this.emailForm.patchValue({
        assignedUserId: this.activeUserId
      });
    }
  }
  get f() { return this.emailForm.controls; }

  rewriteDescriptionWithAi(): void {
    this.aiRewriteError = null;

    const description = String(this.f.description.value ?? '').trim();
    if (!description) {
      this.isGeneratingDraft = false;
      this.aiRewriteError = this.translate.instant('AI_EMAIL_CONTENT_REQUIRED');
      return;
    }

    const apiBaseUrl = environment.apiBaseUrl?.trim();
    if (!apiBaseUrl) {
      this.isGeneratingDraft = false;
      this.aiRewriteError = this.translate.instant('AI_REWRITE_NOT_CONFIGURED');
      return;
    }

    const prompt = String(this.aiRewritePrompt ?? '').trim() || this.translate.instant('AI_DEFAULT_EMAIL_REWRITE_PROMPT');
    const systemContent = String(this.aiRewriteSystemContent ?? '').trim() || 'You are a professional email editor. Return only the rewritten email body, no subject, and keep track of who the email is addressed to and written by. Unless told otherwise, keep the language of the email in English, regardless of the language the prompt was sent in.';
    const requestedTemperature = Number(this.aiRewriteTemperature);
    const temperature = Number.isFinite(requestedTemperature)
      ? Math.min(Math.max(requestedTemperature, 0), 1.4)
      : 0.5;
    this.aiRewriteTemperature = temperature;
    const fromName = String(this.f.fromName.value ?? '').trim();
    const senderFirstName = fromName.split(/\s+/)[0] ?? '';
    const recipientFirstName = String(this.clientData?.firstName ?? '').trim().split(/\s+/)[0] ?? '';
    const recipientContext = recipientFirstName
      ? `The email is addressed to ${recipientFirstName}. Preserve this recipient identity and use the name naturally when appropriate.`
      : '';
    const requestBody = {
      model: this.aiRewriteModel,
      messages: [
        { role: 'system', content: systemContent },
        { role: 'user', content: `${prompt}\n\nThe email is from ${senderFirstName || 'the sender'}. Preserve this sender identity and do not replace it with a placeholder such as [Your Name].${recipientContext ? `\n${recipientContext}` : ''}\n\nEmail body:\n${description}` }
      ],
      ...(this.aiRewriteModel.startsWith('gpt-5')
        ? { max_completion_tokens: this.aiRewriteModel === 'gpt-5' ? 4000 : 2000 }
        : { max_tokens: 800, temperature })
    };

    this.isRewritingWithAi = true;
    const apiUrl = `${apiBaseUrl}/api/chat${this.aiRewriteModel.startsWith('gpt-5') ? '/gpt5' : ''}`;
    this.http.post(apiUrl, requestBody)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isRewritingWithAi = false;
          this.isGeneratingDraft = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          const content = response?.reply || response?.choices?.[0]?.message?.content;
          const rewritten = String(content ?? '').trim();
          if (!rewritten) {
            this.aiRewriteError = this.translate.instant('AI_REWRITE_EMPTY_RESPONSE');
            return;
          }

          this.emailForm.get('description')?.setValue(rewritten);
          this.emailForm.get('description')?.markAsDirty();
        },
        error: () => {
          this.aiRewriteError = this.translate.instant('AI_REWRITE_FAILED');
        }
      });
  }

  activeUserId = '';
  assignedUserFullname = '';
  userEmailAddress = '';

  ngOnInit() {
    this.aiRewritePrompt = String(this.iAiRewritePrompt ?? '').trim() || this.translate.instant('AI_DEFAULT_EMAIL_REWRITE_PROMPT');
    this.aiRewriteSystemContent = String(this.iAiRewriteSystemContent ?? '').trim() || 'You are a professional email editor. Return only the rewritten email body, no subject, and keep track of who the email is addressed to and written by. Unless told otherwise, keep the language of the email in English, regardless of the language the prompt was sent in.';
    // Inputs are guaranteed to be set before ngOnInit.
    this.patchIdsIntoForm();
    this.patchSubjectIntoForm();
    this.syncToClientDropdownFromClientId();
    this.patchDefaultFromName(this._iFromName);

    this.f.title.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((title: string) => {
        this.pendingTitleTerm = '';
        this.applyNoteTemplateByTitle(title);
      });
  }

  ngAfterViewInit(): void {
    // Defer API calls that can toggle global loaders until after the initial change detection.
    setTimeout(() => {
      this.loadDetails();
    }, 0);
  }

  onEnterKey(evt: Event): void {
    const keyboardEvent = evt as KeyboardEvent;
    const target = keyboardEvent?.target as HTMLElement | null;
    const tag = (target?.tagName ?? '').toUpperCase();
    // Allow Enter inside textarea for newlines.
    if (tag === 'TEXTAREA') return;

    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
  }

  onTitleSearch(evt: any): void {
    const term = (typeof evt === 'string') ? evt : (evt?.term ?? '');
    this.pendingTitleTerm = (term ?? '').toString();
  }

  commitTypedTitle(): void {
    const current = this.f.title.value;
    const typed = (this.pendingTitleTerm ?? '').trim();
    if ((current === null || current === undefined || current === '') && typed) {
      this.emailForm.patchValue({ title: typed });
    }
  }

  private isAddMode(): boolean {
    return this.f.id.value === null;
  }

  private applyNoteTemplateByTitle(title: string | null | undefined): void {
    if (!this.isAddMode()) return;
    if (!title) return;
    if (this.applyingTemplate) return;

    const template = this.emailTemplates.find(t => t.title === title);
    if (!template) return;

    this.applyingTemplate = true;
    try {
      const patch: any = {};
      const currentDescription = String(this.f.description.value ?? '').trim();
      const currentSubject = String(this.f.subject.value ?? '').trim();
      const isInternalControl = this.f.isInternal;
      const pinControl = this.f.pin;

      if (!currentDescription && template.description) {
        patch.description = template.description;
      }

      if (!currentSubject && template.subject) {
        patch.subject = template.subject;
      }

      if (!isInternalControl.dirty && typeof template.isInternal === 'boolean') {
        patch.isInternal = template.isInternal;
      }

      if (!pinControl.dirty && typeof template.pin === 'boolean') {
        patch.pin = template.pin;
      }

      if (Object.keys(patch).length === 0) {
        return;
      }

      this.emailForm.patchValue(patch);
    } finally {
      this.applyingTemplate = false;
    }
  }
  loadDetails() {
    const userId = this.f.assignedUserId.value ?? this.activeUserId;
    if (userId) {
      this.usersApiService.getUserDetails(userId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response && response.data) {
            this.assignedUserFullname = response.data.firstName + ' ' + response.data.lastName;
            this.userEmailAddress = this.normalizeReplyToAddress(response.data.email);
            this.emailForm.get('replyTo')?.setValue(this.userEmailAddress);
            if (!this._iFromName) {
              this.patchDefaultFromName(this.assignedUserFullname);
            }
          }
        });
    }

    if (this.iCampaignId) {
      this.campaignService.getCampaignDetails(this.iCampaignId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          // console.log('Campaign Details Response:', response);
          this.campaignData = response.data;
          this.toEmailAddress = response.data.client?.email ?? '';
          this.emailForm.get('to')?.setValue(this.toEmailAddress);
          this.hasEmployees = Array.isArray(response.data.employees) && response.data.employees.length > 0;
        });
    }

    if (this.iClientId) {
      this.clientService.getClientDetails(this.iClientId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          // console.log('Client Details Response:', response);
          this.clientData = response.data;
            if (!this.toEmailAddress) {
              this.toEmailAddress = response.data.email ?? '';
              this.emailForm.get('to')?.setValue(this.toEmailAddress);
              this.hasEmployees = Array.isArray(response.data.employees) && response.data.employees.length > 0;
            }
        });
    }
  }

  onSubmit() {
    this.submitted = true;
    this.submitError = null;
    if (this.emailForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid());
    var request = this.emailForm.value;
    request = this.trimObjectStrings(request);

    // Ensure a createdOn is present for downstream consumers.
    if (!(request as any)?.createdOn) {
      (request as any).createdOn = moment.utc().toDate();
    }

    request.campaignId = this.iCampaignId;
    request.clientId = this.getEffectiveClientId();
    request.leadId = this.iLeadId;
    request.type = this.iType;

    // Notes are displayed based on which id fields are set.
    // If both campaignId and clientId are set, the note will appear on both.
    if (request.type === NOTES_TYPE.Campaigns && request.campaignId) {
      request.clientId = null;
      request.leadId = null;
    }
    if (request.type === NOTES_TYPE.Clients && request.clientId) {
      request.campaignId = null;
      request.leadId = null;
    }
    if (request.type === NOTES_TYPE.Leads && request.leadId) {
      request.campaignId = null;
      request.clientId = null;
    }

    request.addFiles = this.files.filter(x => x.link === undefined).map(x => { return { name: x.name, content: x.content } });
    request.removeFiles = this.removedFiles;

    const emailRequest = this.buildEmailRequest(request);
    if (!emailRequest) {
      this.submitError = this.translate.instant('UNABLE_TO_PREPARE_THE_EMAIL_PAYLOAD_PLEASE_REVIEW_THE_FORM_AND_TRY_AGAIN');
      return;
    }

    this.isSubmitting = true;
    this.dataFeedService.saveNote(request)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.formsApiService.sendEmail(emailRequest)),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.activeModal.close({ saved: true, request });
        },
        error: (error: any) => {
          console.error('Error saving note or sending email:', error);
          this.submitError = this.translate.instant('UNABLE_TO_SAVE_THE_NOTE_AND_SEND_THE_EMAIL_PLEASE_TRY_AGAIN');
        }
      });
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  files: any[] = [];
  removedFiles: any[] = [];
  onSelect(event: any) {
    for (var i = 0; i < event.addedFiles.length; i++) {
      var file = event.addedFiles[i];
      var that = this.files;
      this.readFile(file).then((fileContents: any) => {
        file.content = fileContents;
        that.push(file);
      })
    }
  }

  onRemove(event: any) {
    this.files.splice(this.files.indexOf(event), 1);
    if (event.id) {
      this.removedFiles.push(event.id);
    }
  }

  readFile(file: File): any {
    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        var result: any = (e.target as FileReader).result;
        return resolve(result);
      };
      reader.onerror = e => {
        console.error(`FileReader failed on file ${file.name}.`);
        return reject(null);
      };
      if (!file) {
        console.error('No file to read.');
        return reject(null);
      }
      reader.readAsDataURL(file);
    });
  }
}
